import { SegmentOption } from "components/designV2/segmentcontroller/segmentcontroller";
import { TFunction } from "i18next";
import { CompanyVacationEarningType } from "model/Company";
import {
  decimalToPercent,
  percentToDecimalString,
} from "web-apps/company/components/common/number.normalize.logic";
import {
  ChangedSetting,
  CompanySystemSettingsFormData,
  DiffPlanItem,
  ModuleSettingItem,
  ModuleSettingKey,
  SettingKey,
  SystemSettingsCategory,
} from "./companySystemSettings.types";
import {
  DEFAULT_END_TIME,
  DEFAULT_STAMP_CLOCK_LOGIN_MODE,
  DEFAULT_START_TIME,
  DEFAULT_TZ,
  NONE_ABSENCE_VALUE,
  arrayToStringForBackend,
  normalizeBackendTime,
  stringNormalize,
  toBool,
  toNumber,
} from "./companySystemSettings.logic";

const toWorkPeriodDayCutOff = (value: string | undefined): number => {
  const normalizedValue = toNumber(value, 0);
  return normalizedValue === 0 ||
    (normalizedValue >= 10 && normalizedValue <= 20)
    ? normalizedValue
    : 0;
};

const toCompanyBankAccountType = (
  value: string | undefined,
): "BankAccount" | "InternationalBankAccount" => {
  return value === "InternationalBankAccount"
    ? "InternationalBankAccount"
    : "BankAccount";
};

const toCompanyVacationEarningType = (
  value: string | undefined,
): CompanyVacationEarningType => {
  return value === "CalendarDays" ? "CalendarDays" : "FixedMonthly";
};

export const CATEGORY_SETTING_KEYS: Record<
  SystemSettingsCategory,
  SettingKey[]
> = {
  gigs: [
    "CompanyShowTeamGigModule",
    "CompanyShowPublicGigModule",
    "CompanyGigShiftStartTime",
    "CompanyGigShiftEndTime",
    "GigBreakTime",
    "GigPayDeduction",
    "CompanyWorkerAutoAcceptAllGigs",
  ],
  timeReports: [
    "CompanyTimeReportDeviationThreshold",
    "CompanyTimeReportIncludeFlexTimes",
    "CompanyStampClockAllowEditTime",
  ],
  salary: [
    "EnableCompanySalaryView",
    "CompanyMonthlySalaryIgnoreAbsence",
    "CompanyWorkPeriodDayCutOff",
    "CompanyVacationPayPercentage",
    "CompanyMonthlySalaryVacationPayPercent",
    "CompanyVacationEarningType",
  ],
  stampClock: [
    "CompanyShowStampclockModule",
    "CompanyTimeReportStampBeforeStartThreshold",
    "CompanyTimeReportStampAfterStartThreshold",
    "CompanyTimeReportStampBeforeEndThreshold",
    "CompanyTimeReportStampAfterEndThreshold",
    "LocalStampClock",
    "StampClockAllowAutoGenerate",
    "CompanyTimeStampLoginMode",
  ],
  tips: ["CompanyShowTipModule"],
  chat: ["CompanyShowChatModule"],
  ob: ["CompanyShowInconvenientModule"],
  other: ["CompanyShowChatModule", "CompanyTimeZone"],
  bankAccount: [
    "CompanyBankAgreementId",
    "CompanyBankAccountValue",
    "CompanyBankAccountType",
  ],
};

export const DEFAULT_FORM: CompanySystemSettingsFormData = {
  companyShowInconvenientModule: false,
  companyShowStampclockModule: false,
  companyShowChatModule: false,
  companyShowTipModule: false,
  companyShowTeamGigModule: false,
  companyShowPublicGigModule: false,
  enableCompanySalaryView: false,
  deduction: 0,
  break: 0,
  timeReportDeviationThreshold: 0,
  timeReportStampBeforeStartThreshold: 0,
  timeReportStampAfterStartThreshold: 0,
  timeReportStampBeforeEndThreshold: 0,
  timeReportStampAfterEndThreshold: 0,
  companyTimeReportIncludeFlexTimes: false,
  autoAcceptAllGigs: false,
  companyMonthlySalaryIgnoreAbsence: NONE_ABSENCE_VALUE,
  companyWorkPeriodDayCutOff: 0,
  vacationPayPercentage: 12,
  companyTimeZone: DEFAULT_TZ,
  companyMonthlySalaryVacationPayPercent: 0.43,
  localStampClockSettingActive: false,
  autoGenerateShiftSettingActive: false,
  stampClockLoginMode: DEFAULT_STAMP_CLOCK_LOGIN_MODE,
  companyGigShiftStartTime: DEFAULT_START_TIME,
  companyGigShiftEndTime: DEFAULT_END_TIME,
  companyStampClockAllowEditTime: false,
  companyBankAgreementId: "",
  companyBankAccountValue: "",
  companyBankAccountType: "BankAccount",
  companyVacationEarningType: "FixedMonthly",
};

export const getCategoryOptions = (
  t: TFunction,
): SegmentOption<SystemSettingsCategory>[] => [
  {
    value: "gigs",
    label: t("System.SettingsCategoryShifts"),
  },
  {
    value: "timeReports",
    label: t("System.SettingsCategoryTimeReports"),
  },
  {
    value: "salary",
    label: t("System.SettingsCategorySalary"),
  },
  {
    value: "stampClock",
    label: t("System.SettingsCategoryStampClock"),
  },
  {
    value: "tips",
    label: t("System.SettingsCategoryTips"),
  },
  {
    value: "ob",
    label: t("System.SettingsCategoryOB"),
  },
  {
    value: "bankAccount",
    label: t("System.SettingsCategoryBankAccount"),
  },
  {
    value: "other",
    label: t("System.SettingsCategoryOther"),
  },
];

export const buildInitialFormData = (
  settings: Array<{ key: string; value: string }>,
): CompanySystemSettingsFormData => {
  const getValue = (key: string): string | undefined =>
    settings.find((setting) => setting.key === key)?.value;

  return {
    companyShowInconvenientModule: toBool(
      getValue("CompanyShowInconvenientModule"),
      false,
    ),
    companyShowStampclockModule: toBool(
      getValue("CompanyShowStampclockModule"),
      false,
    ),
    companyShowChatModule: toBool(getValue("CompanyShowChatModule"), false),
    companyShowTipModule: toBool(getValue("CompanyShowTipModule"), false),
    companyShowTeamGigModule: toBool(
      getValue("CompanyShowTeamGigModule"),
      false,
    ),
    companyShowPublicGigModule: toBool(
      getValue("CompanyShowPublicGigModule"),
      false,
    ),
    enableCompanySalaryView: toBool(getValue("EnableCompanySalaryView"), false),
    deduction: toNumber(getValue("GigPayDeduction"), 0),
    break: toNumber(getValue("GigBreakTime"), 0),
    timeReportDeviationThreshold: toNumber(
      getValue("CompanyTimeReportDeviationThreshold"),
      0,
    ),
    timeReportStampBeforeStartThreshold: toNumber(
      getValue("CompanyTimeReportStampBeforeStartThreshold"),
      0,
    ),
    timeReportStampAfterStartThreshold: toNumber(
      getValue("CompanyTimeReportStampAfterStartThreshold"),
      0,
    ),
    timeReportStampBeforeEndThreshold: toNumber(
      getValue("CompanyTimeReportStampBeforeEndThreshold"),
      0,
    ),
    timeReportStampAfterEndThreshold: toNumber(
      getValue("CompanyTimeReportStampAfterEndThreshold"),
      0,
    ),
    companyTimeReportIncludeFlexTimes: toBool(
      getValue("CompanyTimeReportIncludeFlexTimes"),
      false,
    ),
    autoAcceptAllGigs: toBool(
      getValue("CompanyWorkerAutoAcceptAllGigs"),
      false,
    ),
    companyMonthlySalaryIgnoreAbsence: arrayToStringForBackend(
      (getValue("CompanyMonthlySalaryIgnoreAbsence") ?? "")
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
    companyWorkPeriodDayCutOff: toWorkPeriodDayCutOff(
      getValue("CompanyWorkPeriodDayCutOff"),
    ),
    vacationPayPercentage: decimalToPercent(
      toNumber(getValue("CompanyVacationPayPercentage"), 0.12),
      12,
    ),
    companyTimeZone: getValue("CompanyTimeZone") ?? DEFAULT_TZ,
    companyMonthlySalaryVacationPayPercent: decimalToPercent(
      toNumber(getValue("CompanyMonthlySalaryVacationPayPercent"), 0.0043),
      0.43,
    ),
    localStampClockSettingActive: toBool(getValue("LocalStampClock"), false),
    autoGenerateShiftSettingActive: toBool(
      getValue("StampClockAllowAutoGenerate"),
      false,
    ),
    stampClockLoginMode:
      getValue("CompanyTimeStampLoginMode") ?? DEFAULT_STAMP_CLOCK_LOGIN_MODE,
    companyGigShiftStartTime: normalizeBackendTime(
      getValue("CompanyGigShiftStartTime"),
      DEFAULT_START_TIME,
    ),
    companyGigShiftEndTime: normalizeBackendTime(
      getValue("CompanyGigShiftEndTime"),
      DEFAULT_END_TIME,
    ),
    companyStampClockAllowEditTime: toBool(
      getValue("CompanyStampClockAllowEditTime"),
      false,
    ),
    companyBankAgreementId: getValue("CompanyBankAgreementId") ?? "",
    companyBankAccountValue: getValue("CompanyBankAccountValue") ?? "",
    companyBankAccountType: toCompanyBankAccountType(
      getValue("CompanyBankAccountType"),
    ),
    companyVacationEarningType: toCompanyVacationEarningType(
      getValue("CompanyVacationEarningType"),
    ),
  };
};

export const buildDiffPlan = (): DiffPlanItem[] => [
  {
    key: "CompanyShowInconvenientModule",
    pick: (formData) => formData.companyShowInconvenientModule,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyShowStampclockModule",
    pick: (formData) => formData.companyShowStampclockModule,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyShowChatModule",
    pick: (formData) => formData.companyShowChatModule,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyShowTipModule",
    pick: (formData) => formData.companyShowTipModule,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyShowTeamGigModule",
    pick: (formData) => formData.companyShowTeamGigModule,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyShowPublicGigModule",
    pick: (formData) => formData.companyShowPublicGigModule,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "EnableCompanySalaryView",
    pick: (formData) => formData.enableCompanySalaryView,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "GigPayDeduction",
    pick: (formData) => formData.deduction,
    toBackendString: (value) => String(Number(value ?? 0)),
  },
  {
    key: "GigBreakTime",
    pick: (formData) => formData.break,
    toBackendString: (value) => String(Number(value ?? 0)),
  },
  {
    key: "CompanyTimeReportDeviationThreshold",
    pick: (formData) => formData.timeReportDeviationThreshold,
    toBackendString: (value) => String(Number(value ?? 0)),
  },
  {
    key: "CompanyTimeReportStampBeforeStartThreshold",
    pick: (formData) => formData.timeReportStampBeforeStartThreshold,
    toBackendString: (value) => String(Number(value ?? 0)),
  },
  {
    key: "CompanyTimeReportStampAfterStartThreshold",
    pick: (formData) => formData.timeReportStampAfterStartThreshold,
    toBackendString: (value) => String(Number(value ?? 0)),
  },
  {
    key: "CompanyTimeReportStampBeforeEndThreshold",
    pick: (formData) => formData.timeReportStampBeforeEndThreshold,
    toBackendString: (value) => String(Number(value ?? 0)),
  },
  {
    key: "CompanyTimeReportStampAfterEndThreshold",
    pick: (formData) => formData.timeReportStampAfterEndThreshold,
    toBackendString: (value) => String(Number(value ?? 0)),
  },
  {
    key: "CompanyTimeReportIncludeFlexTimes",
    pick: (formData) => formData.companyTimeReportIncludeFlexTimes,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyWorkerAutoAcceptAllGigs",
    pick: (formData) => formData.autoAcceptAllGigs,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyMonthlySalaryIgnoreAbsence",
    pick: (formData) => formData.companyMonthlySalaryIgnoreAbsence,
    toBackendString: (value) => stringNormalize(String(value ?? "")),
  },
  {
    key: "CompanyWorkPeriodDayCutOff",
    pick: (formData) => formData.companyWorkPeriodDayCutOff,
    toBackendString: (value) => String(toWorkPeriodDayCutOff(String(value))),
  },
  {
    key: "CompanyVacationPayPercentage",
    pick: (formData) => formData.vacationPayPercentage,
    toBackendString: (value) => percentToDecimalString(Number(value ?? 12), 12),
  },
  {
    key: "CompanyTimeZone",
    pick: (formData) => formData.companyTimeZone,
    toBackendString: (value) => String(value ?? DEFAULT_TZ),
  },
  {
    key: "CompanyMonthlySalaryVacationPayPercent",
    pick: (formData) => formData.companyMonthlySalaryVacationPayPercent,
    toBackendString: (value) =>
      percentToDecimalString(Number(value ?? 0.43), 0.43),
  },
  {
    key: "LocalStampClock",
    pick: (formData) => formData.localStampClockSettingActive,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "StampClockAllowAutoGenerate",
    pick: (formData) => formData.autoGenerateShiftSettingActive,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyTimeStampLoginMode",
    pick: (formData) => formData.stampClockLoginMode,
    toBackendString: (value) => String(value),
  },
  {
    key: "CompanyGigShiftStartTime",
    pick: (formData) => formData.companyGigShiftStartTime,
    toBackendString: (value) =>
      normalizeBackendTime(String(value ?? ""), DEFAULT_START_TIME),
  },
  {
    key: "CompanyGigShiftEndTime",
    pick: (formData) => formData.companyGigShiftEndTime,
    toBackendString: (value) =>
      normalizeBackendTime(String(value ?? ""), DEFAULT_END_TIME),
  },
  {
    key: "CompanyStampClockAllowEditTime",
    pick: (formData) => formData.companyStampClockAllowEditTime,
    toBackendString: (value) => String(Boolean(value)),
  },
  {
    key: "CompanyBankAgreementId",
    pick: (formData) => formData.companyBankAgreementId,
    toBackendString: (value) => String(value ?? "").trim(),
  },
  {
    key: "CompanyBankAccountValue",
    pick: (formData) => formData.companyBankAccountValue,
    toBackendString: (value) => String(value ?? "").trim(),
  },
  {
    key: "CompanyBankAccountType",
    pick: (formData) => formData.companyBankAccountType,
    toBackendString: (value) =>
      toCompanyBankAccountType(String(value ?? undefined)),
  },
  {
    key: "CompanyVacationEarningType",
    pick: (formData) => formData.companyVacationEarningType,
    toBackendString: (value) =>
      toCompanyVacationEarningType(String(value ?? undefined)),
  },
];

export const getModuleSettingsForCategory = (
  category: SystemSettingsCategory,
  t: TFunction,
): ModuleSettingItem[] => {
  switch (category) {
    case "gigs":
      return [
        {
          key: "companyShowTeamGigModule",
          title: t("CompanySubscription.TeamGigModule"),
          description: t("CompanySubscription.HandleTeamGig"),
        },
        {
          key: "companyShowPublicGigModule",
          title: t("CompanySubscription.PublicGigModule"),
          description: t("CompanySubscription.HandlePublicGig"),
        },
      ];
    case "salary":
      return [
        {
          key: "enableCompanySalaryView",
          title: t("CompanySubscription.SalaryModule"),
          description: t("CompanySubscription.HandleSalary"),
        },
      ];
    case "stampClock":
      return [
        {
          key: "companyShowStampclockModule",
          title: t("CompanySubscription.StampClockModule"),
          description: t("CompanySubscription.HandleStampClock"),
        },
      ];
    case "tips":
      return [
        {
          key: "companyShowTipModule",
          title: t("CompanySubscription.TipModule"),
          description: t("CompanySubscription.HandleTip"),
        },
      ];
    case "ob":
      return [
        {
          key: "companyShowInconvenientModule",
          title: t("System.SettingsCategoryOB"),
          description: t("CompanySubscription.HandleOB"),
        },
      ];
    case "other":
      return [
        {
          key: "companyShowChatModule",
          title: t("System.SettingsCategoryChat"),
          description: t("CompanySubscription.HandleChat"),
        },
      ];
    case "chat":
    case "timeReports":
    case "bankAccount":
      return [];
  }
};

export const getActiveCategoryHasSettings = (
  activeCategory: SystemSettingsCategory,
): boolean => {
  switch (activeCategory) {
    case "tips":
    case "chat":
    case "ob":
      return false;
    case "gigs":
    case "timeReports":
    case "salary":
    case "stampClock":
    case "other":
    case "bankAccount":
      return true;
  }
};

export const getVisibleSettingKeysForCategory = (
  activeCategory: SystemSettingsCategory,
  formData: CompanySystemSettingsFormData,
): SettingKey[] => {
  if (activeCategory === "salary" && !formData.enableCompanySalaryView) {
    return ["EnableCompanySalaryView"];
  }

  if (
    activeCategory === "stampClock" &&
    !formData.companyShowStampclockModule
  ) {
    return ["CompanyShowStampclockModule"];
  }

  return CATEGORY_SETTING_KEYS[activeCategory];
};

export const shouldReloadAfterSavingSettings = (
  successfulSettings: ChangedSetting[],
): boolean =>
  successfulSettings.some(
    (setting) =>
      setting.value === "true" &&
      (setting.key === "EnableCompanySalaryView" ||
        setting.key === "CompanyShowStampclockModule" ||
        setting.key === "CompanyShowTipModule"),
  );

export const resetHiddenSettingsForDisabledModule = (
  key: ModuleSettingKey,
  formData: CompanySystemSettingsFormData,
  savedSettings: CompanySystemSettingsFormData,
): CompanySystemSettingsFormData => {
  if (key === "enableCompanySalaryView") {
    return {
      ...formData,
      companyMonthlySalaryIgnoreAbsence:
        savedSettings.companyMonthlySalaryIgnoreAbsence,
      companyWorkPeriodDayCutOff: savedSettings.companyWorkPeriodDayCutOff,
      vacationPayPercentage: savedSettings.vacationPayPercentage,
      companyMonthlySalaryVacationPayPercent:
        savedSettings.companyMonthlySalaryVacationPayPercent,
      companyVacationEarningType: savedSettings.companyVacationEarningType,
    };
  }

  if (key === "companyShowStampclockModule") {
    return {
      ...formData,
      timeReportStampBeforeStartThreshold:
        savedSettings.timeReportStampBeforeStartThreshold,
      timeReportStampAfterStartThreshold:
        savedSettings.timeReportStampAfterStartThreshold,
      timeReportStampBeforeEndThreshold:
        savedSettings.timeReportStampBeforeEndThreshold,
      timeReportStampAfterEndThreshold:
        savedSettings.timeReportStampAfterEndThreshold,
      localStampClockSettingActive: savedSettings.localStampClockSettingActive,
      autoGenerateShiftSettingActive:
        savedSettings.autoGenerateShiftSettingActive,
      stampClockLoginMode: savedSettings.stampClockLoginMode,
    };
  }

  return formData;
};

export const getChangedSettings = (
  formData: CompanySystemSettingsFormData,
  baseline: CompanySystemSettingsFormData,
  diffPlan: DiffPlanItem[],
): ChangedSetting[] => {
  const changedSettings: ChangedSetting[] = [];

  diffPlan.forEach(({ key, pick, toBackendString }) => {
    const currentValue = toBackendString(pick(formData));
    const baselineValue = toBackendString(pick(baseline));

    if (currentValue !== baselineValue) {
      changedSettings.push({ key, value: currentValue });
    }
  });

  return changedSettings;
};

export const updateSavedSettingsForKeys = (
  changedKeys: SettingKey[],
  formData: CompanySystemSettingsFormData,
  savedSettings: CompanySystemSettingsFormData,
): CompanySystemSettingsFormData =>
  changedKeys.reduce<CompanySystemSettingsFormData>(
    (nextSavedSettings, changedKey) => {
      switch (changedKey) {
        case "CompanyShowInconvenientModule":
          return {
            ...nextSavedSettings,
            companyShowInconvenientModule:
              formData.companyShowInconvenientModule,
          };
        case "CompanyShowStampclockModule":
          return {
            ...nextSavedSettings,
            companyShowStampclockModule: formData.companyShowStampclockModule,
          };
        case "CompanyShowChatModule":
          return {
            ...nextSavedSettings,
            companyShowChatModule: formData.companyShowChatModule,
          };
        case "CompanyShowTipModule":
          return {
            ...nextSavedSettings,
            companyShowTipModule: formData.companyShowTipModule,
          };
        case "CompanyShowTeamGigModule":
          return {
            ...nextSavedSettings,
            companyShowTeamGigModule: formData.companyShowTeamGigModule,
          };
        case "CompanyShowPublicGigModule":
          return {
            ...nextSavedSettings,
            companyShowPublicGigModule: formData.companyShowPublicGigModule,
          };
        case "EnableCompanySalaryView":
          return {
            ...nextSavedSettings,
            enableCompanySalaryView: formData.enableCompanySalaryView,
          };
        case "GigPayDeduction":
          return {
            ...nextSavedSettings,
            deduction: formData.deduction,
          };
        case "GigBreakTime":
          return {
            ...nextSavedSettings,
            break: formData.break,
          };
        case "CompanyTimeReportDeviationThreshold":
          return {
            ...nextSavedSettings,
            timeReportDeviationThreshold: formData.timeReportDeviationThreshold,
          };
        case "CompanyTimeReportStampBeforeStartThreshold":
          return {
            ...nextSavedSettings,
            timeReportStampBeforeStartThreshold:
              formData.timeReportStampBeforeStartThreshold,
          };
        case "CompanyTimeReportStampAfterStartThreshold":
          return {
            ...nextSavedSettings,
            timeReportStampAfterStartThreshold:
              formData.timeReportStampAfterStartThreshold,
          };
        case "CompanyTimeReportStampBeforeEndThreshold":
          return {
            ...nextSavedSettings,
            timeReportStampBeforeEndThreshold:
              formData.timeReportStampBeforeEndThreshold,
          };
        case "CompanyTimeReportStampAfterEndThreshold":
          return {
            ...nextSavedSettings,
            timeReportStampAfterEndThreshold:
              formData.timeReportStampAfterEndThreshold,
          };
        case "CompanyTimeReportIncludeFlexTimes":
          return {
            ...nextSavedSettings,
            companyTimeReportIncludeFlexTimes:
              formData.companyTimeReportIncludeFlexTimes,
          };
        case "CompanyWorkerAutoAcceptAllGigs":
          return {
            ...nextSavedSettings,
            autoAcceptAllGigs: formData.autoAcceptAllGigs,
          };
        case "CompanyMonthlySalaryIgnoreAbsence":
          return {
            ...nextSavedSettings,
            companyMonthlySalaryIgnoreAbsence:
              formData.companyMonthlySalaryIgnoreAbsence,
          };
        case "CompanyWorkPeriodDayCutOff":
          return {
            ...nextSavedSettings,
            companyWorkPeriodDayCutOff: formData.companyWorkPeriodDayCutOff,
          };
        case "CompanyVacationPayPercentage":
          return {
            ...nextSavedSettings,
            vacationPayPercentage: formData.vacationPayPercentage,
          };
        case "CompanyTimeZone":
          return {
            ...nextSavedSettings,
            companyTimeZone: formData.companyTimeZone,
          };
        case "CompanyMonthlySalaryVacationPayPercent":
          return {
            ...nextSavedSettings,
            companyMonthlySalaryVacationPayPercent:
              formData.companyMonthlySalaryVacationPayPercent,
          };
        case "LocalStampClock":
          return {
            ...nextSavedSettings,
            localStampClockSettingActive: formData.localStampClockSettingActive,
          };
        case "StampClockAllowAutoGenerate":
          return {
            ...nextSavedSettings,
            autoGenerateShiftSettingActive:
              formData.autoGenerateShiftSettingActive,
          };
        case "CompanyTimeStampLoginMode":
          return {
            ...nextSavedSettings,
            stampClockLoginMode: formData.stampClockLoginMode,
          };
        case "CompanyGigShiftStartTime":
          return {
            ...nextSavedSettings,
            companyGigShiftStartTime: formData.companyGigShiftStartTime,
          };
        case "CompanyGigShiftEndTime":
          return {
            ...nextSavedSettings,
            companyGigShiftEndTime: formData.companyGigShiftEndTime,
          };
        case "CompanyStampClockAllowEditTime":
          return {
            ...nextSavedSettings,
            companyStampClockAllowEditTime:
              formData.companyStampClockAllowEditTime,
          };
        case "CompanyBankAgreementId":
          return {
            ...nextSavedSettings,
            companyBankAgreementId: formData.companyBankAgreementId,
          };
        case "CompanyBankAccountValue":
          return {
            ...nextSavedSettings,
            companyBankAccountValue: formData.companyBankAccountValue,
          };
        case "CompanyBankAccountType":
          return {
            ...nextSavedSettings,
            companyBankAccountType: formData.companyBankAccountType,
          };
        case "CompanyVacationEarningType":
          return {
            ...nextSavedSettings,
            companyVacationEarningType: formData.companyVacationEarningType,
          };
      }

      return nextSavedSettings;
    },
    savedSettings,
  );
